import type {
  DeploymentRunKind,
  DeploymentStep,
  ImplementationEvent,
  NativePolicy,
  NativePolicyGroup,
  NativePolicyLogic,
  NativePolicyState,
  PolicyAlignmentState,
  PolicyDefinitionState,
  PolicyImplementation,
  PolicyImplementationState,
} from "@/api/temp/AgentEstateService";
import {
  isDataPlatformPolicyId,
  type NativePolicyBlueprint,
} from "./materialize";
import { createScenarioSwitch } from "./scenarioSwitch";

/**
 * Mock asynchronous deployment of a definition's native policies.
 *
 * Like the onboarding run (`phases.ts`), a deployment is a pure function of
 * wall-clock time since its run started - nothing ticks, nothing is written
 * while it progresses. The store records a run when a definition is applied,
 * reapplied or retried; every read derives each native policy's state from
 * the runs and `now`. So closing a panel, reloading, or opening the same
 * definition from another page can never restart or skip a deployment.
 *
 * `?deployment=success|partial|failed` picks a deterministic outcome inside
 * mock scope, the way `?enforcement=` picks readiness. Failures are never
 * random: `partial` always fails the same data-platform control.
 */

export interface PolicyDeploymentTiming {
  createDelayMs: number;
  deployDelayMs: number;
  verifyDelayMs: number;
  /** Data-platform controls start this long after TLX policies are created. */
  platformLagMs: number;
}

/** Demo pacing: about four seconds from Apply to verified. */
export const MOCK_POLICY_DEPLOYMENT_TIMING: Readonly<PolicyDeploymentTiming> = {
  createDelayMs: 900,
  deployDelayMs: 1800,
  verifyDelayMs: 900,
  platformLagMs: 400,
};

/** Specs pin this so an Apply is deployed by the next read. */
export const INSTANT_POLICY_DEPLOYMENT_TIMING: Readonly<PolicyDeploymentTiming> =
  { createDelayMs: 0, deployDelayMs: 0, verifyDelayMs: 0, platformLagMs: 0 };

let timing: PolicyDeploymentTiming = { ...MOCK_POLICY_DEPLOYMENT_TIMING };

const getPolicyDeploymentTiming = (): PolicyDeploymentTiming => ({
  ...timing,
});

/** Applies to runs started afterwards - a run keeps the timing it began with. */
export function setPolicyDeploymentTiming(
  next: Partial<PolicyDeploymentTiming>,
): void {
  timing = { ...timing, ...next };
}


/* ------------------------------------------------------------- scenario */

export type DeploymentScenario = "success" | "partial" | "failed";

const scenario = createScenarioSwitch<DeploymentScenario>({
  param: "deployment",
  storageKey: "__TLX_DEPLOYMENT_SCENARIO__",
  values: ["success", "partial", "failed"],
  fallback: "success",
});

/** Specs and demos pin a scenario; `undefined` clears the pin. */
export const setDeploymentScenario = scenario.set;

/** A `?deployment=` param wins and is remembered for the session. */
export const getDeploymentScenario = scenario.get;

/* ------------------------------------------------------------------ runs */

/** One apply, reapply or retry, as the store persists it. */
export interface DeploymentRun {
  id: string;
  kind: DeploymentRunKind;
  startedAt: string;
  timing: PolicyDeploymentTiming;
  /** The native policies this run creates or redeploys. */
  childIds: string[];
  /**
   * The subset that did not exist before this run. Everything else in
   * `childIds` is an update of a native policy that already existed.
   */
  createdIds: string[];
  /** Decided when the run starts, so the outcome never changes mid-run. */
  failingIds: string[];
}

/** An individual override of one native policy. */
export interface NativeOverride {
  current: NativePolicyLogic;
  modifiedAt: string;
}

/**
 * Which of a run's native policies fail. `partial` fails the last control of
 * the first data platform (or, with none, the last tool policy); `failed`
 * fails everything; a retry always succeeds.
 */
function failingChildren(
  scenario: DeploymentScenario,
  kind: DeploymentRunKind,
  children: NativePolicyBlueprint[],
): string[] {
  if (kind === "RETRY" || scenario === "success" || !children.length) return [];
  if (scenario === "failed") return children.map(child => child.id);
  const platform = children.filter(child => child.engine === "DATA_PLATFORM");
  const firstGroup = platform.filter(
    child => child.groupId === platform[0]?.groupId,
  );
  const tools = children.filter(child => child.target === "MCP_TOOL");
  const victim = firstGroup.at(-1) ?? tools.at(-1) ?? children.at(-1)!;
  return [victim.id];
}

export function startRun(
  kind: DeploymentRunKind,
  children: NativePolicyBlueprint[],
  now: number,
  {
    createdIds = [],
    scenario = getDeploymentScenario(),
  }: { createdIds?: string[]; scenario?: DeploymentScenario } = {},
): DeploymentRun {
  return {
    id: `${kind.toLowerCase()}-${now}`,
    kind,
    startedAt: new Date(now).toISOString(),
    timing: getPolicyDeploymentTiming(),
    childIds: children.map(child => child.id),
    createdIds,
    failingIds: failingChildren(scenario, kind, children),
  };
}

interface Timeline {
  /** Leaves QUEUED. */
  creatingAt: number;
  deployingAt: number;
  settledAt: number;
}

/**
 * TLX policies are created at once; data-platform controls wait for the TLX
 * create step, then trail by `platformLagMs`.
 */
function timelineFor(run: DeploymentRun, childId: string): Timeline {
  const start = Date.parse(run.startedAt);
  const { createDelayMs, deployDelayMs, platformLagMs } = run.timing;
  if (!isDataPlatformPolicyId(childId))
    return {
      creatingAt: start,
      deployingAt: start + createDelayMs,
      settledAt: start + createDelayMs + deployDelayMs,
    };
  const creatingAt = start + createDelayMs;
  return {
    creatingAt,
    deployingAt: creatingAt + platformLagMs,
    settledAt: creatingAt + platformLagMs + deployDelayMs,
  };
}

const settledAtOf = (run: DeploymentRun) =>
  Math.max(
    Date.parse(run.startedAt),
    ...run.childIds.map(id => timelineFor(run, id).settledAt),
  );

/** When verification finishes and the run stops being in flight. */
export const runCompletesAt = (run: DeploymentRun) =>
  settledAtOf(run) + run.timing.verifyDelayMs;

function childStateAt(
  run: DeploymentRun,
  childId: string,
  now: number,
): NativePolicyState {
  const { creatingAt, deployingAt, settledAt } = timelineFor(run, childId);
  if (now < creatingAt) return "QUEUED";
  if (now < deployingAt) return "CREATING";
  if (now < settledAt) return "DEPLOYING";
  return run.failingIds.includes(childId) ? "FAILED" : "DEPLOYED";
}

const sameLogic = (left: NativePolicyLogic, right: NativePolicyLogic) =>
  left.effect === right.effect &&
  left.subject.trim() === right.subject.trim() &&
  left.rule.trim() === right.rule.trim();

const failureFor = (child: NativePolicyBlueprint) =>
  child.engine === "DATA_PLATFORM"
    ? `${child.resource.platform || child.groupName} rejected ${child.name}: the TrustLogix service role lacks the privilege to apply it.`
    : `The MCP Gateway did not acknowledge ${child.name}. It was created in the TrustLogix Policy Engine but not published.`;

const iso = (ms: number) => new Date(ms).toISOString();

/* -------------------------------------------------------------- resolve */

export interface ImplementationInput {
  definitionId: string;
  definitionName: string;
  environmentId: string;
  blueprints: NativePolicyBlueprint[];
  /** Oldest first. */
  runs: DeploymentRun[];
  /**
   * When the definition was first assigned. Native policies no run covers
   * predate deployment tracking (seeded assignments) and read as deployed then.
   */
  baselineAt?: string;
  overrides: ReadonlyMap<string, NativeOverride>;
  now: number;
}

function resolveChild(
  input: ImplementationInput,
  child: NativePolicyBlueprint,
  started: DeploymentRun[],
): NativePolicy {
  const covering = started.filter(run => run.childIds.includes(child.id));
  // No run created it: it predates deployment tracking (a seeded assignment)
  // and was deployed then. Later runs covering it are updates.
  const predates =
    !!input.baselineAt &&
    !input.runs.some(run => run.createdIds.includes(child.id));
  let state: NativePolicyState = predates ? "DEPLOYED" : "QUEUED";
  let createdAt = predates ? input.baselineAt : undefined;
  let deployedAt = createdAt;
  for (const [index, run] of covering.entries()) {
    const timeline = timelineFor(run, child.id);
    state = childStateAt(run, child.id, input.now);
    if (index === 0 && !predates && state !== "QUEUED" && state !== "CREATING")
      createdAt = iso(timeline.deployingAt);
    if (state === "DEPLOYED") deployedAt = iso(timeline.settledAt);
  }

  // An override holds until a run started after it redeploys this policy.
  const override = input.overrides.get(child.id);
  const overwritten =
    !!override &&
    covering.some(
      run =>
        Date.parse(run.startedAt) > Date.parse(override.modifiedAt) &&
        childStateAt(run, child.id, input.now) === "DEPLOYED",
    );
  const effective =
    override && !overwritten && !sameLogic(override.current, child.expected)
      ? override
      : undefined;

  return {
    id: child.id,
    parentDefinitionId: input.definitionId,
    parentDefinitionName: input.definitionName,
    environmentId: input.environmentId,
    name: child.name,
    target: child.target,
    engine: child.engine,
    groupId: child.groupId,
    targetKey: child.targetKey,
    resource: child.resource,
    state,
    source: effective ? "MANUALLY_MODIFIED" : "GENERATED",
    expected: child.expected,
    current: effective?.current ?? child.expected,
    failure: state === "FAILED" ? failureFor(child) : undefined,
    createdAt,
    deployedAt,
    modifiedAt: effective?.modifiedAt,
    registryServerId: child.registryServerId,
  };
}

const IN_PROGRESS: NativePolicyState[] = ["QUEUED", "CREATING", "DEPLOYING"];

function rollup(
  policies: NativePolicy[],
  verifying: boolean,
): PolicyImplementationState {
  if (!policies.length) return "NOT_CREATED";
  const pending = policies.filter(policy => IN_PROGRESS.includes(policy.state));
  // Phase follows what is in flight; policies an update does not touch are
  // already deployed and say nothing about it.
  if (pending.length)
    return pending.some(policy => policy.state === "DEPLOYING")
      ? "DEPLOYING"
      : "CREATING";
  if (verifying) return "DEPLOYING";
  const failed = policies.filter(policy => policy.state === "FAILED").length;
  if (!failed) return "DEPLOYED";
  return failed === policies.length ? "FAILED" : "PARTIALLY_DEPLOYED";
}

function stepsFor(
  run: DeploymentRun,
  blueprints: NativePolicyBlueprint[],
  now: number,
): DeploymentStep[] {
  const start = Date.parse(run.startedAt);
  const { createDelayMs, deployDelayMs } = run.timing;
  const inRun = blueprints.filter(child => run.childIds.includes(child.id));
  const tlx = inRun.filter(child => child.engine === "TLX_POLICY_ENGINE");
  const data = inRun.filter(child => child.engine === "DATA_PLATFORM");
  const failed = (children: NativePolicyBlueprint[]) =>
    children.some(child => run.failingIds.includes(child.id));
  const phase = (from: number, to: number, children: NativePolicyBlueprint[]) =>
    now < from
      ? ("PENDING" as const)
      : now < to
        ? ("ACTIVE" as const)
        : failed(children)
          ? ("FAILED" as const)
          : ("DONE" as const);

  const steps: DeploymentStep[] = [];
  if (tlx.length) {
    steps.push({
      key: "CREATE_TLX",
      status: now < start + createDelayMs ? "ACTIVE" : "DONE",
      policyCount: tlx.length,
    });
    steps.push({
      key: "DEPLOY_TLX",
      status: phase(start + createDelayMs, start + createDelayMs + deployDelayMs, tlx),
      policyCount: tlx.length,
    });
  }
  if (data.length) {
    const settled = Math.max(
      ...data.map(child => timelineFor(run, child.id).settledAt),
    );
    steps.push({
      key: "DEPLOY_PLATFORM",
      status: phase(start + createDelayMs, settled, data),
      policyCount: data.length,
      resources: [...new Set(data.map(child => child.groupName))],
    });
  }
  const settledAt = settledAtOf(run);
  steps.push({
    key: "VERIFY",
    status: phase(settledAt, runCompletesAt(run), inRun),
    policyCount: inRun.length,
  });
  return steps;
}

function historyFor(
  input: ImplementationInput,
  started: DeploymentRun[],
  policies: NativePolicy[],
): ImplementationEvent[] {
  const { now, blueprints } = input;
  const events: ImplementationEvent[] = [];
  const push = (at: number, event: Omit<ImplementationEvent, "id" | "occurredAt">) => {
    if (at <= now)
      events.push({
        ...event,
        id: `${event.kind}-${at}-${event.label ?? ""}`,
        occurredAt: iso(at),
      });
  };
  const created = new Set(input.runs.flatMap(run => run.createdIds));
  const baselineCount = blueprints.filter(child => !created.has(child.id)).length;
  if (input.baselineAt && baselineCount)
    push(Date.parse(input.baselineAt), { kind: "APPLIED", count: baselineCount });

  started.forEach(run => {
    const start = Date.parse(run.startedAt);
    const inRun = blueprints.filter(child => run.childIds.includes(child.id));
    const ok = (child: NativePolicyBlueprint) => !run.failingIds.includes(child.id);
    push(start, {
      kind:
        run.kind === "APPLY" ? "APPLIED" : run.kind === "REAPPLY" ? "REAPPLIED" : "RETRIED",
      count: inRun.length,
    });
    const tlx = inRun.filter(child => child.engine === "TLX_POLICY_ENGINE");
    if (tlx.some(ok))
      push(start + run.timing.createDelayMs + run.timing.deployDelayMs, {
        kind: "TLX_DEPLOYED",
        count: tlx.filter(ok).length,
      });
    const groups = [
      ...new Set(
        inRun.filter(child => child.engine === "DATA_PLATFORM").map(child => child.groupId),
      ),
    ];
    groups.forEach(groupId => {
      const controls = inRun.filter(child => child.groupId === groupId);
      if (controls.some(ok))
        push(Math.max(...controls.map(child => timelineFor(run, child.id).settledAt)), {
          kind: "PLATFORM_DEPLOYED",
          count: controls.filter(ok).length,
          label: controls[0].groupName,
        });
    });
    const failing = inRun.filter(child => !ok(child));
    if (failing.length)
      push(settledAtOf(run), {
        kind: "DEPLOYMENT_FAILED",
        count: failing.length,
        label: failing[0].name,
      });
    else push(runCompletesAt(run), { kind: "VERIFIED", count: inRun.length });
  });

  const names = new Map(policies.map(policy => [policy.id, policy.name]));
  input.overrides.forEach((override, id) => {
    if (names.has(id))
      push(Date.parse(override.modifiedAt), { kind: "MODIFIED", label: names.get(id) });
  });
  return events.sort(
    (left, right) =>
      Date.parse(right.occurredAt) - Date.parse(left.occurredAt) ||
      left.id.localeCompare(right.id),
  );
}

/**
 * The definition's implementation at `now`: each native policy's state folded
 * over the runs that cover it, then rolled up. Same input, same output - there
 * is nothing to restart.
 */
export function buildImplementation(
  input: ImplementationInput,
): PolicyImplementation {
  const started = input.runs.filter(run => Date.parse(run.startedAt) <= input.now);
  const latest = started.at(-1);
  const inFlight = !!latest && input.now < runCompletesAt(latest);
  const policies = input.blueprints.map(child =>
    resolveChild(input, child, started),
  );

  const groupIds = [...new Set(policies.map(policy => policy.groupId))];
  const groups: NativePolicyGroup[] = groupIds.map(groupId => {
    const members = policies.filter(policy => policy.groupId === groupId);
    const first = input.blueprints.find(child => child.groupId === groupId)!;
    return {
      id: groupId,
      engine: first.engine,
      name: first.groupName,
      technology: first.resource.technology,
      platform: first.resource.platform,
      platformId: first.resource.platformId,
      registryServerId: first.registryServerId,
      state: rollup(
        members,
        inFlight && members.some(policy => latest!.childIds.includes(policy.id)),
      ),
      serverPolicyCount: members.filter(policy => policy.target === "MCP_SERVER").length,
      toolPolicyCount: members.filter(policy => policy.target === "MCP_TOOL").length,
      controlCount: members.filter(policy => policy.target === "DATA_PLATFORM").length,
      deployedCount: members.filter(policy => policy.state === "DEPLOYED").length,
      failedCount: members.filter(policy => policy.state === "FAILED").length,
      modifiedCount: members.filter(policy => policy.source === "MANUALLY_MODIFIED").length,
      policies: members,
    };
  });

  const count = (predicate: (policy: NativePolicy) => boolean) =>
    policies.filter(predicate).length;
  const tlx = (policy: NativePolicy) => policy.engine === "TLX_POLICY_ENGINE";
  const deployed = (policy: NativePolicy) => policy.state === "DEPLOYED";
  const modified = policies.filter(policy => policy.source === "MANUALLY_MODIFIED");
  const failed = policies.filter(policy => policy.state === "FAILED");

  const implementationState = rollup(policies, inFlight);
  const alignment: PolicyAlignmentState = !policies.some(policy => policy.deployedAt)
    ? "UNKNOWN"
    : modified.length
      ? "MODIFIED"
      : "MATCHES_DEFINITION";
  const definitionState: PolicyDefinitionState =
    inFlight && latest?.kind === "APPLY"
      ? "APPLYING"
      : implementationState === "FAILED"
        ? "FAILED"
        : "APPLIED";

  return {
    definitionId: input.definitionId,
    definitionName: input.definitionName,
    environmentId: input.environmentId,
    definitionState,
    implementationState,
    alignment,
    inFlight,
    run: latest && { id: latest.id, kind: latest.kind, startedAt: latest.startedAt },
    steps: latest ? stepsFor(latest, input.blueprints, input.now) : [],
    groups,
    counts: {
      total: policies.length,
      mcpServerPolicies: count(policy => policy.target === "MCP_SERVER"),
      toolPolicies: count(policy => policy.target === "MCP_TOOL"),
      tlxPolicies: count(tlx),
      dataControls: count(policy => !tlx(policy)),
      deployed: count(deployed),
      tlxDeployed: count(policy => tlx(policy) && deployed(policy)),
      failed: failed.length,
      modified: modified.length,
    },
    modified,
    failed,
    history: historyFor(input, started, policies),
  };
}
